// src/pages/PhotoProcessingPage.jsx
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Alert,
  Modal,
  Spinner,
  ProgressBar,
  Badge
} from 'react-bootstrap';
import { uploadImages, processImages } from '../api/photoProcessingApi';
import { yoloModelOptions, getYoloModelLabel } from '../constants/yoloModels';
import { useTranslation } from 'react-i18next';
import './PhotoProcessingPage.css';

const PhotoProcessingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // State management
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadedImages, setUploadedImages] = useState({});
  const [processing, setProcessing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingProgress, setProcessingProgress] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [showParametersModal, setShowParametersModal] = useState(false);

  // Processing parameters
  const [parameters, setParameters] = useState({
    confidenceThreshold: 0.3,
    iouThreshold: 0.5,
    maxDetections: 96,
    saveProcessedImage: true,
    yolo_model_type: 0,
    enable_plate_detection: true// Global default
  });

  // Per-file YOLO model selection
  const [perFileYoloModels, setPerFileYoloModels] = useState({});

  // Detect whether a filename contains `token` as a standalone piece
  // (surrounded by non-alphanumeric separators like space, '.', '_', '-', or string edges)
  const hasSeparatedToken = (filename, token) => {
    const escaped = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(^|[^a-zA-Zа-яА-ЯёЁ0-9])${escaped}(?=[^a-zA-Zа-яА-ЯёЁ0-9]|$)`, 'i');
    return regex.test(filename);
  };

  // Auto-select the YOLO model type based on the file name
  const detectYoloModelFromFilename = (filename) => {
    if (!filename) return 0; // Auto

    if (filename.includes('254') || filename.includes('366')) {
      return 3; // 254&366
    }

    if (hasSeparatedToken(filename, 'IR') || hasSeparatedToken(filename, 'ИК')) {
      return 2; // IR
    }

    return 0; // Auto
  };

  // File selection handlers
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 50 * 1024 * 1024; // 50MB limit
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      setError('Some files were skipped. Only image files under 50MB are allowed.');
    } else {
      setError('');
    }

    // Initialize per-file models, auto-detected from each file name
    const newModels = {};
    validFiles.forEach((file, index) => {
      newModels[`file_${index}`] = detectYoloModelFromFilename(file.name);
    });
    setPerFileYoloModels(newModels);

    setSelectedFiles(validFiles);
    setUploadedImages({});
    setSuccess('');
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const files = Array.from(event.dataTransfer.files);
    const validFiles = files.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 50 * 1024 * 1024;
      return isValidType && isValidSize;
    });

    if (validFiles.length !== files.length) {
      setError('Some files were skipped. Only image files under 50MB are allowed.');
    } else {
      setError('');
    }

    // Initialize per-file models, auto-detected from each file name
    const newModels = {};
    validFiles.forEach((file, index) => {
      newModels[`file_${index}`] = detectYoloModelFromFilename(file.name);
    });
    setPerFileYoloModels(newModels);

    setSelectedFiles(validFiles);
    setUploadedImages({});
    setSuccess('');
  };

  const removeFile = (index) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);

    // Remove from per-file models
    const newModels = { ...perFileYoloModels };
    delete newModels[`file_${index}`];

    // Reindex remaining models
    const reindexedModels = {};
    newFiles.forEach((_, newIndex) => {
      const oldKey = `file_${newIndex >= index ? newIndex + 1 : newIndex}`;
      reindexedModels[`file_${newIndex}`] = newModels[oldKey] || parameters.yolo_model_type;
    });
    setPerFileYoloModels(reindexedModels);

    if (newFiles.length === 0) {
      setUploadedImages({});
      setError('');
      setSuccess('');
    }
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    setUploadedImages({});
    setPerFileYoloModels({});
    setError('');
    setSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle per-file YOLO model change
  const handlePerFileModelChange = (fileKey, value) => {
    setPerFileYoloModels(prev => ({
      ...prev,
      [fileKey]: parseInt(value)
    }));
  };

  // Apply global model to all files
  const handleApplyGlobalModel = () => {
    const newModels = {};
    selectedFiles.forEach((_, index) => {
      newModels[`file_${index}`] = parameters.yolo_model_type;
    });
    setPerFileYoloModels(newModels);
  };

  // Upload handlers
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setError('Please select at least one image file.');
      return;
    }

    setUploading(true);
    setError('');
    setUploadProgress(0);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file, index) => {
        formData.append(`image_${index}`, file);
      });

      const response = await uploadImages(formData);

      if (response.success) {
        setUploadedImages(response.files);
        setSuccess(`Successfully uploaded ${Object.keys(response.files).length} images.`);
        setUploadProgress(100);
      } else {
        throw new Error(response.error || 'Upload failed');
      }
    } catch (err) {
      setError(`Upload failed: ${err.message}`);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  // Processing handlers
  const handleShowParameters = () => {
    if (Object.keys(uploadedImages).length === 0) {
      setError('Please upload images first.');
      return;
    }
    setShowParametersModal(true);
  };

  const handleParameterChange = (param, value) => {
    setParameters(prev => ({
      ...prev,
      [param]: value
    }));
  };

  const handleStartProcessing = async () => {
    setShowParametersModal(false);
    setProcessing(true);
    setError('');

    try {
      // Prepare yolo_model_type array for backend
      const yoloModelArray = selectedFiles.map((_, index) => {
        const fileKey = `file_${index}`;
        return perFileYoloModels[fileKey] !== undefined
          ? perFileYoloModels[fileKey]
          : parameters.yolo_model_type;
      });

      const config = {
        ...parameters,
        yolo_model_type: yoloModelArray, // Send as array
        enable_plate_detection: parameters.enable_plate_detection
      };
      const response = await processImages(uploadedImages, config);

      if (response.success) {
        // Redirect to jobs page where users can see all their jobs
        navigate('/jobs');
      } else {
        throw new Error('Failed to start processing');
      }
    } catch (err) {
      setError(`Processing failed: ${err.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Helper functions
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFilePreview = (file) => {
    return URL.createObjectURL(file);
  };

  return (
    <div className="photo-processing-page container mt-4">
      <Row>
        <Col>
          <h2 className="mb-4">Photo Processing</h2>
        </Col>
      </Row>

      {/* Error/Success Messages */}
      {error && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {success && (
        <Row className="mb-3">
          <Col>
            <Alert variant="success" dismissible onClose={() => setSuccess('')}>
              {success}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        {/* File Selection Panel */}
        <Col md={6}>
          <Card className="h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Select Images</h5>
              {selectedFiles.length > 0 && (
                <Button variant="outline-secondary" size="sm" onClick={clearAllFiles}>
                  Clear All
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {/* File Drop Zone */}
              <div
                className="file-drop-zone"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="drop-zone-content">
                  <i className="bi bi-cloud-upload fs-1 text-muted"></i>
                  <p className="mt-2 mb-1">
                    <strong>Click to select files</strong> or drag and drop
                  </p>
                  <p className="text-muted small">
                    Supported formats: JPG, PNG, GIF, BMP, TIFF<br/>
                    Maximum size: 300MB
                    Maximum size per file: 50MB
                  </p>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              {/* Selected Files List */}
              {selectedFiles.length > 0 && (
                <div className="selected-files mt-3">
                  <h6>Selected Files ({selectedFiles.length})</h6>
                  <div className="files-list">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="file-item">
                        <div className="file-preview">
                          <img
                            src={getFilePreview(file)}
                            alt={file.name}
                            className="preview-thumbnail"
                          />
                        </div>
                        <div className="file-info">
                          <div className="file-name">{file.name}</div>
                          <div className="file-size text-muted">
                            {formatFileSize(file.size)}
                          </div>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeFile(index)}
                        >
                          <i className="bi bi-trash"></i>
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Upload & Processing Panel */}
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>
              <h5 className="mb-0">Upload & Process</h5>
            </Card.Header>
            <Card.Body>
              {/* Upload Section */}
              <div className="upload-section mb-4">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6>Step 1: Upload Images</h6>
                  <Badge bg={Object.keys(uploadedImages).length > 0 ? 'success' : 'secondary'}>
                    {Object.keys(uploadedImages).length > 0 ? 'Uploaded' : 'Pending'}
                  </Badge>
                </div>

                {uploading && (
                  <ProgressBar
                    animated
                    now={uploadProgress}
                    label={`${uploadProgress}%`}
                    className="mb-2"
                  />
                )}

                <Button
                  variant="primary"
                  onClick={handleUpload}
                  disabled={selectedFiles.length === 0 || uploading}
                  className="w-100"
                >
                  {uploading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-cloud-upload me-2"></i>
                      Upload Images ({selectedFiles.length})
                    </>
                  )}
                </Button>
              </div>

              {/* Processing Section */}
              <div className="processing-section">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6>Step 2: Start Processing</h6>
                  <Badge bg={processing ? 'warning' : 'secondary'}>
                    {processing ? 'Processing' : 'Ready'}
                  </Badge>
                </div>

                <Button
                  variant="success"
                  onClick={handleShowParameters}
                  disabled={Object.keys(uploadedImages).length === 0 || processing}
                  className="w-100"
                >
                  {processing ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-gear me-2"></i>
                      Configure & Process
                    </>
                  )}
                </Button>
              </div>

              {/* Current Parameters Display */}
              {Object.keys(uploadedImages).length > 0 && (
                <div className="current-params mt-4">
                    <h6>Current Parameters</h6>
                    <div className="params-display">
                        <div>Confidence Threshold: {parameters.confidenceThreshold}</div>
                        <div>IoU Threshold: {parameters.iouThreshold}</div>
                        <div>Global Model: {getYoloModelLabel(parameters.yolo_model_type)}</div>
                        <div>Plate Detection: {parameters.enable_plate_detection ? 'Enabled' : 'Disabled'}</div>
                        <div>Per-file Models: {
                            selectedFiles.length === 1
                                ? getYoloModelLabel(perFileYoloModels.file_0 || parameters.yolo_model_type)
                                : `${selectedFiles.length} files configured`
                        }</div>
                    </div>
                </div>
            )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Parameters Modal */}
      <Modal show={showParametersModal} onHide={() => setShowParametersModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Processing Parameters</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Confidence Threshold
                    <span className="text-muted ms-2">({parameters.confidenceThreshold})</span>
                  </Form.Label>
                  <Form.Range
                    min={0.1}
                    max={1.0}
                    step={0.05}
                    value={parameters.confidenceThreshold}
                    onChange={(e) => handleParameterChange('confidenceThreshold', parseFloat(e.target.value))}
                  />
                  <Form.Text className="text-muted">
                    Minimum confidence score for detections (0.1 - 1.0)
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    IoU Threshold
                    <span className="text-muted ms-2">({parameters.iouThreshold})</span>
                  </Form.Label>
                  <Form.Range
                    min={0.1}
                    max={1.0}
                    step={0.05}
                    value={parameters.iouThreshold}
                    onChange={(e) => handleParameterChange('iouThreshold', parseFloat(e.target.value))}
                  />
                  <Form.Text className="text-muted">
                    Intersection over Union threshold for NMS (0.1 - 1.0)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Plate Detection</Form.Label>
                  <div>
                    <Form.Check
                      type="switch"
                      id="enable-plate-detection"
                      label={parameters.enable_plate_detection ? "Enabled" : "Disabled"}
                      checked={parameters.enable_plate_detection}
                      onChange={(e) => handleParameterChange('enable_plate_detection', e.target.checked)}
                    />
                    <Form.Text className="text-muted">
                      When enabled, the system will detect and crop the plate region before cell detection.
                      Disable to process the entire image without plate detection.
                    </Form.Text>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            {/* Global YOLO Model Type Selection */}
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Global Model Type</Form.Label>
                  <div className="d-flex align-items-center mb-2">
                    <Form.Select
                      value={parameters.yolo_model_type}
                      onChange={(e) => handleParameterChange('yolo_model_type', parseInt(e.target.value))}
                      className="me-2"
                    >
                      {yoloModelOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleApplyGlobalModel}
                    >
                      Apply to All Files
                    </Button>
                  </div>
                  <Form.Text className="text-muted">
                    Select default model type for all files. Click "Apply to All Files" to update individual file settings.
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {/* Per-file model configuration */}
            {selectedFiles.length > 0 && (
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Per-file Model Configuration ({selectedFiles.length} files)</Form.Label>
                    <div className="per-file-models border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                              <img
                                src={getFilePreview(file)}
                                alt={file.name}
                                style={{ width: '30px', height: '30px', objectFit: 'cover', marginRight: '10px' }}
                              />
                              <span className="small">{file.name}</span>
                            </div>
                            <Form.Select
                              size="sm"
                              value={perFileYoloModels[`file_${index}`] || parameters.yolo_model_type}
                              onChange={(e) => handlePerFileModelChange(`file_${index}`, e.target.value)}
                              style={{ width: '150px' }}
                            >
                              {yoloModelOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Form.Select>
                          </div>
                        </div>
                      ))}
                    </div>
                    <Form.Text className="text-muted">
                      Configure individual model for each file. Files will use their individual settings.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowParametersModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleStartProcessing}>
            <i className="bi bi-play-fill me-2"></i>
            Start Processing
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PhotoProcessingPage;